/**
 * Browser-side OpenTelemetry tracing.
 *
 * Public surface:
 *   - initTelemetry(config)  Boot the SDK. No-op when config.enabled is false.
 *   - startSpan(name, attrs) Start a child of the current active span.
 *   - withSpan(name, fn)     Convenience: start a span around an async block.
 *   - shutdownTelemetry()    Flush pending spans (for beforeunload).
 *
 * Architectural notes:
 *
 *   - The exporter always posts to /api/telemetry/v1/traces on the same origin.
 *     The backend proxies to the configured SaaS endpoint and injects the API
 *     key server-side, so no secrets ever ship to the browser.
 *
 *   - We use the *minimum* OTel surface area: the official @opentelemetry/api
 *     for span management, a TracerProvider, BatchSpanProcessor for batching,
 *     and the OTLP/HTTP+JSON exporter. No auto-instrumentations — every span
 *     in this app is created explicitly so we control the noise floor.
 *
 *   - `traceparent` propagation: api.ts wraps each fetch() call in a span and
 *     reads the active span via this module's `currentTraceparentHeader()`
 *     helper, then sets it on the outgoing request. The PHP backend reads
 *     HTTP_TRACEPARENT and continues the trace.
 */

import { context, propagation, trace, type Span, type SpanOptions } from '@opentelemetry/api';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { Resource } from '@opentelemetry/resources';
import {
  BatchSpanProcessor,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';

import { BufferingSpanExporter } from './telemetryBuffer.js';

export interface TelemetryConfig {
  enabled: boolean;
  serviceName: string;
  sampleRate: number;
}

let initialized = false;
let provider: WebTracerProvider | null = null;
let bufferedExporter: BufferingSpanExporter | null = null;

/**
 * Boot the SDK if config.enabled is true. Safe to call more than once; the
 * second call is a no-op so a hot-reloaded dev page doesn't double-instrument.
 */
export function initTelemetry(config: TelemetryConfig): void {
  if (initialized || !config.enabled) {
    initialized = true;
    return;
  }

  const exporter = new OTLPTraceExporter({
    // Absolute same-origin URL: the OTLP exporter validates the endpoint with
    // `new URL(...)`, which rejects a bare relative path like '/api/...'.
    // Resolving against the current origin keeps it same-origin (so the proxy's
    // cookie/session auth applies — the session cookie rides along automatically)
    // while satisfying the parser.
    url: new URL('/api/telemetry/v1/traces', window.location.origin).toString(),
  });

  // The proxy is auth-gated, so wrap the exporter to buffer spans until login
  // and flush them via markTelemetryAuthenticated(). Pre-login spans (bootstrap,
  // the login flow) are preserved instead of 401'd away.
  bufferedExporter = new BufferingSpanExporter(exporter);

  provider = new WebTracerProvider({
    resource: new Resource({
      [ATTR_SERVICE_NAME]: `${config.serviceName}-browser`,
    }),
    sampler: new TraceIdRatioBasedSampler(config.sampleRate),
    spanProcessors: [new BatchSpanProcessor(bufferedExporter, {
      // Small batches + short delay so latency-sensitive UX events arrive
      // promptly. The browser tab is short-lived; long delays risk losing
      // traces on navigation.
      maxQueueSize: 100,
      scheduledDelayMillis: 2000,
    })],
  });

  provider.register({
    propagator: new W3CTraceContextPropagator(),
  });

  initialized = true;

  // Best-effort flush on tab close. shutdown() returns a promise but we can't
  // await it — modern browsers fire `pagehide` synchronously.
  window.addEventListener('pagehide', () => {
    void shutdownTelemetry();
  });
}

/**
 * Start a span. When no init has happened (or telemetry disabled), this returns
 * the OTel API's noop span and call sites work unchanged.
 */
export function startSpan(name: string, options?: SpanOptions): Span {
  return trace.getTracer('mapnmove-browser').startSpan(name, options);
}

/**
 * Run an async block inside a new span; the span ends automatically and
 * records any thrown exception.
 */
export async function withSpan<T>(
  name: string,
  fn: (span: Span) => Promise<T>,
  options?: SpanOptions,
): Promise<T> {
  const span = startSpan(name, options);
  return await context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const out = await fn(span);
      return out;
    } catch (err) {
      if (err instanceof Error) {
        span.recordException(err);
      }
      span.setStatus({ code: 2 /* ERROR */, message: String(err) });
      throw err;
    } finally {
      span.end();
    }
  });
}

/**
 * Get the W3C `traceparent` header for the currently active span, or null if
 * no span is active or telemetry is disabled. Used by api.ts to inject the
 * header on outgoing fetch() calls so the backend can continue the trace.
 */
export function currentTraceparentHeader(): string | null {
  const carrier: Record<string, string> = {};
  propagation.inject(context.active(), carrier);
  return carrier['traceparent'] ?? null;
}

/**
 * Mark the session authenticated and flush any spans buffered before login.
 * Call this once a session exists (after a successful login, or when an
 * existing session is detected on page load). No-op when telemetry is disabled.
 */
export function markTelemetryAuthenticated(): void {
  bufferedExporter?.markAuthenticated();
}

/** Flush pending spans. Awaited by pagehide handlers. */
export async function shutdownTelemetry(): Promise<void> {
  if (provider !== null) {
    try {
      await provider.shutdown();
    } catch {
      // Telemetry shutdown failures must never break the page.
    }
  }
  bufferedExporter = null;
}
